// ============================================================================
// CHAT + CAPTURE GLYPHS — transcribed path-for-path from
// docs/design/handoff/"LifeOS Chat.dc.html" (options 11a, 11b, 11c).
//
// Only the glyphs those three frames draw that no other screen already has.
// `Sparkle`, `SparkleSmall` and `RedditFace` are NOT re-drawn here: they exist
// in ../interests/icons.tsx with the same paths, and a second copy is how two
// sparkles end up a hair apart.
//
// COLOUR COMES FROM THE TOKEN, NEVER FROM A PROP — every path is
// stroke="currentColor" and the caller sets `text-chat`, `text-success-dot`,
// `text-warning-2`. That is what keeps scripts/ship-gate.sh's brand audit
// satisfiable; a raw hex in here would fail the build.
// ============================================================================

import type { IconProps } from '../icons'

function Glyph({
  size,
  strokeWidth,
  className,
  d,
}: Required<Pick<IconProps, 'size' | 'strokeWidth'>> & { className?: string; d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

/** The `New` button's plus. Drawn at 11px / 2.4 in the mockup — a heavier cross. */
export const Plus = ({ size = 11, strokeWidth = 2.4, className }: IconProps) => (
  <Glyph size={size} strokeWidth={strokeWidth} className={className} d="M12 6v12M6 12h12" />
)

/** The capture palette's leading plus, 22px / 1.8 — a longer, lighter cross than the button's. */
export const PlusThin = ({ size = 22, strokeWidth = 1.8, className }: IconProps) => (
  <Glyph size={size} strokeWidth={strokeWidth} className={className} d="M12 5.4v13.2M5.4 12h13.2" />
)

/** The composer's and the palette's microphone. */
export const Mic = ({ size = 17, strokeWidth = 1.8, className }: IconProps) => (
  <Glyph
    size={size}
    strokeWidth={strokeWidth}
    className={className}
    d="M12 4.6a2.4 2.4 0 0 1 2.4 2.4v4.4a2.4 2.4 0 0 1-4.8 0V7A2.4 2.4 0 0 1 12 4.6M6.6 11.4a5.4 5.4 0 0 0 10.8 0M12 16.8V20"
  />
)

/** Send. */
export const ArrowUp = ({ size = 14, strokeWidth = 2.2, className }: IconProps) => (
  <Glyph size={size} strokeWidth={strokeWidth} className={className} d="M12 19V5M6 11l6-6 6 6" />
)

/** The scope-limit strip's warning circle. */
/**
 * The attach glyph — a paperclip, at the mic's own weight so the two sit level in the composer.
 * NOT in the handoff: Chat v3 added sending a picture, and the frames predate it. Drawn in the
 * same 24-grid, same stroke, same `currentColor` rule as everything else in this file.
 */
export const Paperclip = ({ size = 16, strokeWidth = 1.8, className }: IconProps) => (
  <Glyph
    size={size}
    strokeWidth={strokeWidth}
    className={className}
    d="M21.44 11.05 12.25 20.24a5.5 5.5 0 0 1-7.78-7.78l8.49-8.49a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a1.5 1.5 0 0 1-2.12-2.12l7.78-7.78"
  />
)

export const AlertCircle = ({ size = 13, strokeWidth = 2, className }: IconProps) => (
  <Glyph
    size={size}
    strokeWidth={strokeWidth}
    className={className}
    d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4M12 8.4h.01M12 11.6v4.4"
  />
)

/**
 * `Jump to timestamp`, on a transcript source card. Two paths rather than one, exactly as the
 * mockup draws it: a triangle inside a circle.
 */
export const PlayCircle = ({ size = 10, strokeWidth = 2, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9.6 8.4 17 12l-7.4 3.6z" />
    <path d="M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4" />
  </svg>
)

/**
 * The journal starter card's book (11b). A different glyph from the rail's `Book`: this one is
 * open-edged on the right, which is what makes it read as a page rather than a closed cover.
 */
export const BookOpen = ({ size = 15, strokeWidth = 1.8, className }: IconProps) => (
  <Glyph
    size={size}
    strokeWidth={strokeWidth}
    className={className}
    d="M5 4.6A1.6 1.6 0 0 1 6.6 3H18v18H6.6A1.6 1.6 0 0 1 5 19.4zM5 17.6h13"
  />
)
